import type { SvgIconComponent } from "@mui/icons-material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import type { Me } from "@app/api-client";

/**
 * 使用指南（全站功能手冊）的目錄。
 *
 * 分三組：
 *
 *   開始使用  general   所有人 —— 登入、導覽、個人中心這類不屬於任何服務的事
 *   服務      services  依 `/me/` 列出使用者進得去的服務，**不在這裡宣告**
 *   後台管理  admin     只有 staff／superuser 看得到 —— 一般使用者進不了 Django Admin
 *
 * 「服務」那一組刻意不寫死：前端不維護服務清單，服務的指南跟著 `/me/` 走，
 * 第八個服務上線時指南目錄自動多一列（內容還沒寫就是空狀態）。
 *
 * 網址每一組都有自己的前綴（`/account/guide/<組>/<key>`），不要把服務代碼直接
 * 掛在 `/account/guide/` 底下 —— 服務代碼由後端決定，哪天有服務叫 `admin`
 * 就會跟後台那一組撞在一起。
 *
 * `key` 同時是網址片段與之後 Markdown 內容的檔名（`<組>/<key>.md`），改名會讓
 * 使用者存下來的連結失效，所以定了就不要改。
 */

export type GuideGroupKey = "general" | "services" | "admin";

export interface GuideTopic {
  key: string;
  label: string;
  /** 目錄列上的一句話：這一篇教什麼，而不是這個功能是什麼 */
  description: string;
  icon: SvgIconComponent;
}

export const GUIDE_ROOT = "/account/guide";

export const GENERAL_TOPICS: GuideTopic[] = [
  {
    key: "sign-in",
    label: "登入與登出",
    description: "用 email 或手機號碼登入、在個人中心登出，以及忘記密碼時該找誰。",
    icon: LoginOutlinedIcon,
  },
  {
    key: "navigation",
    label: "導覽與版面",
    description: "總覽、服務、通知、個人中心四個分節怎麼切換，以及手機版的底部列。",
    icon: ExploreOutlinedIcon,
  },
  {
    key: "account",
    label: "個人中心",
    description: "查看自己的帳號狀態、基本資料與在各服務裡的角色，以及修改自己的密碼。",
    icon: PersonOutlineOutlinedIcon,
  },
];

export const ADMIN_TOPICS: GuideTopic[] = [
  {
    key: "users",
    label: "使用者帳號",
    description: "在後台建立與停用帳號、維護基本資料、替使用者重設密碼。",
    icon: ManageAccountsOutlinedIcon,
  },
  {
    key: "roles",
    label: "服務與角色",
    description: "把使用者加進服務、指派角色，以及每個角色能做哪些事。",
    icon: AdminPanelSettingsOutlinedIcon,
  },
  {
    key: "services",
    label: "服務設定",
    description: "修改服務在畫面上顯示的名稱與說明。",
    icon: TuneOutlinedIcon,
  },
];

/**
 * 看得到「後台管理」那一組的人。
 *
 * 跟後端能不能登入 Django Admin 的條件一致（is_staff；superuser 一定可以）。
 * 這只是不讓一般使用者看到用不到的教學，不是安全邊界 —— 真正的權限在後端。
 */
export function canSeeAdminGuide(me: Pick<Me, "is_staff" | "is_superuser"> | null | undefined) {
  return Boolean(me?.is_staff || me?.is_superuser);
}

export function guideHref(group: GuideGroupKey, key: string): string {
  return `${GUIDE_ROOT}/${group}/${key}`;
}

/** 一句話描述某個服務的指南 —— 不沿用服務自己的說明，那是「服務是什麼」不是「教什麼」 */
export function serviceGuideDescription(serviceName: string): string {
  return `${serviceName}各個頁面的操作方式與常見問題。`;
}
